import { volcaColor, volcaColorAlpha } from "./spreadsheet-theme";

// Flags a cell that carries a persistent manual edit with a bold right-triangle
// in its top-right corner — a punchier take on the admin grid's edited-cell
// corner marker. Painted on the grid canvas via the viewer's drawCell, on top
// of the already-rendered cell content, so it never tints the cell background
// or clips its text. A soft drop shadow lifts it off the cell so it draws the
// eye; a thin light stroke on the exposed diagonal keeps the edge crisp.
const MARKER_SIZE_PX = 13;
const copiedRangeBorderColor = () => volcaColor("--teal-10");

export const drawCopiedRangeBorder = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  cell: { col: number; row: number },
  range: { x: number; y: number; width: number; height: number },
): void => {
  const isInsideRange =
    cell.col >= range.x &&
    cell.col < range.x + range.width &&
    cell.row >= range.y &&
    cell.row < range.y + range.height;
  if (!isInsideRange) return;

  const isTop = cell.row === range.y;
  const isBottom = cell.row === range.y + range.height - 1;
  const isLeft = cell.col === range.x;
  const isRight = cell.col === range.x + range.width - 1;
  if (!isTop && !isBottom && !isLeft && !isRight) return;

  const inset = 1;
  const left = rect.x + inset;
  const right = rect.x + rect.width - inset;
  const top = rect.y + inset;
  const bottom = rect.y + rect.height - inset;

  ctx.save();
  ctx.beginPath();
  if (isTop) {
    ctx.moveTo(left, top);
    ctx.lineTo(right, top);
  }
  if (isRight) {
    ctx.moveTo(right, top);
    ctx.lineTo(right, bottom);
  }
  if (isBottom) {
    ctx.moveTo(right, bottom);
    ctx.lineTo(left, bottom);
  }
  if (isLeft) {
    ctx.moveTo(left, bottom);
    ctx.lineTo(left, top);
  }
  ctx.setLineDash([5, 3]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = copiedRangeBorderColor();
  ctx.stroke();
  ctx.restore();
};

export const drawOverrideCornerMarker = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  color: string,
): void => {
  const right = rect.x + rect.width;
  const topRight = { x: right, y: rect.y };
  const left = { x: right - MARKER_SIZE_PX, y: rect.y };
  const bottom = { x: right, y: rect.y + MARKER_SIZE_PX };

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(left.x, left.y);
  ctx.lineTo(topRight.x, topRight.y);
  ctx.lineTo(bottom.x, bottom.y);
  ctx.closePath();

  // Soft lift so the marker pops off the cell. The tint is the darkest neutral
  // rather than a blue-black, so the shadow stays hueless over the coloured
  // fills a generated payroll sheet paints.
  ctx.shadowColor = volcaColorAlpha("--sand-12", 0.35);
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = -1;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = color;
  ctx.fill();

  // Crisp light edge on the exposed diagonal (no shadow on the stroke).
  ctx.shadowColor = "transparent";
  ctx.beginPath();
  ctx.moveTo(left.x, left.y);
  ctx.lineTo(bottom.x, bottom.y);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = volcaColorAlpha("--on-brand", 0.9);
  ctx.stroke();
  ctx.restore();
};

// Flags a cell with an open comment thread: the same triangle as the edit
// marker, but in the top-LEFT corner so a commented edit shows both.
const commentMarkerColor = () => volcaColor("--orange-9");

export const drawCommentCornerMarker = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
): void => {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(rect.x, rect.y);
  ctx.lineTo(rect.x + MARKER_SIZE_PX, rect.y);
  ctx.lineTo(rect.x, rect.y + MARKER_SIZE_PX);
  ctx.closePath();
  ctx.shadowColor = volcaColorAlpha("--sand-12", 0.35);
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = commentMarkerColor();
  ctx.fill();

  ctx.shadowColor = "transparent";
  ctx.beginPath();
  ctx.moveTo(rect.x + MARKER_SIZE_PX, rect.y);
  ctx.lineTo(rect.x, rect.y + MARKER_SIZE_PX);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = volcaColorAlpha("--on-brand", 0.9);
  ctx.stroke();
  ctx.restore();
};

/**
 * Repaints a selected column's header grey.
 *
 * Glide fills a selected header with `accentColor` at full size, not as a ring
 * — so selecting a column put a solid blue block above the sheet, louder than
 * anything else on screen and louder than the selection it was describing. Grey
 * says the same thing quietly, and continues the ramp the other header states
 *
 * Done here rather than in the theme because `accentColor` is shared with the
 * cell selection ring, which stays blue. Glide fills the header before handing
 * it to this callback, so this paints over that fill; the title is then drawn by
 * Glide in `textHeaderSelected`, which the theme sets to normal ink to match.
 */
export const drawSelectedHeaderBackground = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  /**
   * RANGE merely passes through. Glide marks only the anchor cell's column, so
   * dragging a block across five columns lit one header and left the other four
   * at rest — the axis stopped describing the selection as soon as it grew past
   * a single cell.
   */
  isWholeColumnSelected = true,
): void =>
  fillCellBackground(
    ctx,
    rect,
    volcaColor(
      isWholeColumnSelected ? "--grid-header-selected" : "--grid-header-focus",
    ),
  );

/**
 * Tints the row-number gutter cell of the row holding the selected cell.
 *
 * Glide marks only one axis: selecting a cell fills its column header with
 * `bgHeaderHasFocus`, while the gutter draws its number flat, in `textLight`,
 * whatever is selected. So the grid told you which column you were in and never
 * which row. Every other spreadsheet marks both, and on a sheet this wide the
 * row is the harder one to keep track of.
 *
 * read as one crosshair rather than two unrelated highlights.
 *
 * Painted inside the row-marker renderer because the viewer's `drawCell` cannot
 * reach here: marker cells are "inner only", and Glide skips the draw callback
 * for them entirely.
 */
export const drawSelectedRowMarkerBackground = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  /**
   * is selected. Without the second step a fully selected row kept the rest
   * fill and took Glide's blue row wash over it, so the two axes disagreed —
   * selecting a column greyed its header, selecting a row tinted its gutter blue.
   */
  isWholeRowSelected = false,
): void =>
  fillCellBackground(
    ctx,
    rect,
    volcaColor(
      isWholeRowSelected ? "--grid-header-selected" : "--grid-header-focus",
    ),
  );

const fillCellBackground = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  color: string,
): void => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  ctx.restore();
};
